import { usePrevious } from 'hooks/usePrevious';
import { useCallback, useEffect, useRef } from 'react';
import { ExpressionSpecification, Map } from 'shared/map-exports';

const PULSE_CYCLE_SECONDS = 1.5;
const PULSE_AMPLITUDE = 10;

type UsePulseAnimationProps = {
  /** Base radius as a number or Mapbox expression for data-driven sizing */
  baseRadius: number | ExpressionSpecification;
  isActive: boolean;
  /** A single layer ID, or multiple if needed */
  layerId: string | string[];
  map?: Map;
  maxZoomLevel: number;
  minZoomLevel: number;
};

export const usePulseAnimation = (props: UsePulseAnimationProps): void => {
  const { baseRadius, isActive, layerId, map, maxZoomLevel, minZoomLevel } =
    props;
  const animationRef = useRef<number | null>(null);
  const startTimeRef = useRef<number | null>(null);
  const prevIsActive = usePrevious(isActive);

  const setCircleRadius = useCallback(
    (radius: number | ExpressionSpecification) => {
      if (!map?.isStyleLoaded()) return;

      try {
        const layerIds = Array.isArray(layerId) ? layerId : [layerId];

        // NOTE:
        // It's fine to call setPaintProperty on all pulse layers, even if some
        // are outside their minzoom/maxzoom range. Mapbox keeps non-visible
        // layers in the style but skips rendering them, so these updates add
        // minimal overhead while keeping the pulse animation continuous when
        // transitioning between zoom levels.
        layerIds.forEach((id) => {
          map.setPaintProperty(id, 'circle-radius', radius);
        });
      } catch (error) {
        console.error('usePulseAnimation:setCircleRadius', error);
      }
    },
    [layerId, map],
  );

  const stop = useCallback(() => {
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }
    startTimeRef.current = null;
    setCircleRadius(0);
  }, [setCircleRadius]);

  const start = useCallback(() => {
    if (!map) return;

    stop(); // Clean up existing animations
    startTimeRef.current = null;

    const animate = (timestamp: number): void => {
      if (!startTimeRef.current) {
        startTimeRef.current = timestamp;
      }

      const elapsedTimeSeconds = (timestamp - startTimeRef.current) / 1000;
      const currentCycle = elapsedTimeSeconds % PULSE_CYCLE_SECONDS;
      // Oscillates between 0 and PULSE_AMPLITUDE * 2 as cycle progresses
      const pulseOffset =
        Math.sin((currentCycle / PULSE_CYCLE_SECONDS) * Math.PI * 2) *
          PULSE_AMPLITUDE +
        PULSE_AMPLITUDE;

      // Calculate base radius based on zoom (matches the point icon circle-radius style expression)
      const zoom = map.getZoom();
      // Normalized progress (0-1)
      const zoomProgress =
        (zoom - minZoomLevel) / (maxZoomLevel - minZoomLevel);
      // Clamp to [0,1] range
      const clampedProgress = Math.max(0, Math.min(1, zoomProgress));

      if (typeof baseRadius === 'number') {
        // Simple number: calculate radius directly
        const radius = baseRadius + clampedProgress * baseRadius * 2;
        setCircleRadius(radius + pulseOffset);
      } else {
        // Expression: build a Mapbox expression that adds the zoom factor and pulse offset
        const zoomMultiplier = 1 + clampedProgress * 2;
        const radiusExpression: ExpressionSpecification = [
          '+',
          ['*', baseRadius, zoomMultiplier],
          pulseOffset,
        ];
        setCircleRadius(radiusExpression);
      }

      animationRef.current = requestAnimationFrame(animate);
    };

    // Start the animation loop
    animationRef.current = requestAnimationFrame(animate);
  }, [baseRadius, map, maxZoomLevel, minZoomLevel, setCircleRadius, stop]);

  useEffect(() => {
    if (!prevIsActive && isActive) {
      start();
    } else if (prevIsActive && !isActive) {
      stop();
    }
  }, [isActive, prevIsActive, start, stop]);

  useEffect(() => {
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);
};
